import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { getOfferteSettings, saveOfferteSettings } from '../../api/offerte';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardFooter, CardDescription } from '../ui/card';
import { AlertCircle, CheckCircle, Save, ArrowLeft, Settings, FileText, Info } from 'lucide-react';
import { Alert, AlertDescription } from '../ui/alert';

// Color scheme constants
const COLORS = {
  primary: "#BFFF00", // Neon green
  secondary: "#1E4FCF", // Blue
  accent: "#FF6B00", // Orange
  dark: "#05081A", // Dark blue/black
}

const OfferteSettingsForm = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    offerteGeldigheidRange: 30,
    extraTekst: '',
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      try {
        const data = await getOfferteSettings();
        if (data) {
          setSettings({
            offerteGeldigheidRange: data.offerteGeldigheidRange || 30,
            extraTekst: data.extraTekst || '',
          });
        }
        setError(null);
      } catch (err) {
        setError(err.message || 'Kon offerte-instellingen niet laden.');
        console.error("Error fetching offerte settings:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: name === 'offerteGeldigheidRange' ? parseInt(value, 10) || 0 : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccessMessage('');
    try {
      await saveOfferteSettings(settings);
      setSuccessMessage('Instellingen succesvol opgeslagen!');
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      setError(err.message || 'Kon instellingen niet opslaan.');
      console.error("Error saving offerte settings:", err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="relative">
          <div className="w-12 h-12 rounded-full border-3 border-gray-100 border-t-[#BFFF00] border-r-[#BFFF00] animate-spin"></div>
          <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
            <span className="text-xs font-medium text-gray-400">LADEN</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="space-y-6 p-4 md:p-6"
    >
      <div className="flex justify-between items-center">
        <div className="relative">
          <h1 className="text-2xl font-bold text-gray-900">Offerte Instellingen</h1>
          <div className="absolute bottom-0 left-0 w-1/2 h-1 bg-[#BFFF00] -z-10 transform translate-y-1 opacity-60"></div>
        </div>
        <Button 
          variant="outline" 
          onClick={() => navigate('/dashboard/offertes')}
          className="border-gray-200 text-gray-700 hover:bg-gray-50/80"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Terug naar overzicht
        </Button>
      </div>
      
      {error && (
        <Alert variant="destructive" className="border border-red-200 bg-red-50 text-red-800 rounded-lg">
          <div className="flex gap-2 items-center">
            <AlertCircle className="h-4 w-4 text-red-500" />
            <AlertDescription className="font-medium">{error}</AlertDescription>
          </div>
        </Alert>
      )}

      {successMessage && (
        <Alert className="border border-[#BFFF00] bg-[#BFFF00]/10 text-[#05081A] rounded-lg">
          <div className="flex gap-2 items-center">
            <CheckCircle className="h-4 w-4 text-[#05081A]" />
            <AlertDescription className="font-medium">{successMessage}</AlertDescription>
          </div>
        </Alert>
      )}

      <Card className="border border-gray-200 shadow-sm rounded-lg overflow-hidden">
        <CardHeader className="bg-white border-b border-gray-100 p-6">
          <div className="flex items-center gap-3">
           
            <div>
              <CardTitle className="text-lg font-bold text-gray-900">Standaardinstellingen voor offertes</CardTitle>
              <CardDescription className="text-sm text-gray-500">
                Deze instellingen worden gebruikt bij het aanmaken van nieuwe offertes.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-6 p-6 bg-white">
            <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-start gap-3">
              <Info className="h-5 w-5 text-[#1E4FCF] mt-0.5" />
              <p className="text-sm text-gray-700">
                Pas hier de standaardinstellingen aan die worden gebruikt bij het aanmaken van nieuwe offertes. 
                Deze instellingen kunnen altijd nog per offerte worden aangepast.
              </p>
            </div>

            <div className="grid gap-6">
              <div className="space-y-2">
                <Label htmlFor="offerteGeldigheidRange" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <FileText className="h-3.5 w-3.5 text-[#1E4FCF]" />
                  Geldigheid Offertes (dagen)
                </Label>
                <Input
                  id="offerteGeldigheidRange"
                  name="offerteGeldigheidRange"
                  type="number"
                  value={settings.offerteGeldigheidRange}
                  onChange={handleChange}
                  min="1"
                  className="border-gray-200 focus:border-[#BFFF00] focus:ring-[#BFFF00]/10 rounded-md"
                />
                <p className="text-sm text-gray-500">
                  Standaard aantal dagen dat een offerte geldig is na de offertedatum.
                </p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="extraTekst" className="text-sm font-medium text-gray-700 flex items-center gap-2">
                  <FileText className="h-3.5 w-3.5 text-[#1E4FCF]" />
                  Standaard Extra Tekst op Offerte
                </Label>
                <Textarea
                  id="extraTekst"
                  name="extraTekst"
                  value={settings.extraTekst}
                  onChange={handleChange}
                  rows={4}
                  placeholder="Bijv. betalingsvoorwaarden, algemene opmerkingen..."
                  className="border-gray-200 focus:border-[#BFFF00] focus:ring-[#BFFF00]/10 rounded-md"
                />
                <p className="text-sm text-gray-500">
                  Deze tekst wordt standaard toegevoegd aan nieuwe offertes en zal zichtbaar zijn voor uw klanten.
                </p>
              </div>
            </div>
          </CardContent>
          <CardFooter className="flex justify-end gap-3 p-6 bg-gray-50 border-t border-gray-100">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => navigate('/dashboard/offertes')} 
              disabled={saving}
              className="border-gray-300 text-gray-700 hover:bg-gray-50 rounded-md"
            >
              Annuleren
            </Button>
            <Button 
              type="submit" 
              disabled={saving || !!successMessage} 
              className="bg-[#BFFF00] hover:bg-[#BFFF00]/90 text-[#05081A] font-medium rounded-md"
            >
              {saving ? (
                <div className="flex items-center">
                  <div className="h-4 w-4 rounded-full border-2 border-[#05081A]/20 border-t-[#05081A] animate-spin mr-2"></div>
                  <span>Bezig met opslaan...</span>
                </div>
              ) : (
                <div className="flex items-center">
                  <Save className="mr-2 h-4 w-4" /> Instellingen Opslaan
                </div>
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </motion.div>
  );
};

export default OfferteSettingsForm;